import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Shield, Zap, Brain, Code2, MessageSquare,
  Database, Bot, Server, Lock,
  ArrowLeft, Globe, Cpu, Send,
} from "lucide-react";
import { profileAPI } from "../services/api";

interface PlanFeature { feat_name: string; desc_name: string; value?: unknown; }
interface PlanFromAPI {
  id: number; name: string; display_name: string; price_monthly: number;
  features?: PlanFeature[] | Record<string, unknown>;
  is_default?: boolean; is_active?: boolean;
}

const PD = "0123456789".split("").reduce((acc, d, i) => ({ ...acc, [d]: "0123456789"[i] }), {} as Record<string,string>);
const PDIGITS = "0123456789";
const FDIGITS = "\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9";
const toPersianNum = (n: number | string) =>
  String(n).replace(/[0-9]/g, (d) => FDIGITS[parseInt(d, 10)]);
const formatFa = (n: number) =>
  toPersianNum(Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "\u066C"));

function getFeature(plan: PlanFromAPI, key: string): string {
  const f = plan.features;
  const raw = Array.isArray(f)
    ? f.find((x) => x?.feat_name === key)?.value
    : f && typeof f === "object" ? (f as Record<string, unknown>)[key] : undefined;
  if (raw === null || raw === undefined) return "\u2014";
  if (typeof raw === "boolean") return raw ? "\u0628\u0644\u0647" : "\u062E\u06CC\u0631";
  return String(raw);
}
function getPlanPrice(p: PlanFromAPI) {
  const d = getFeature(p, "price_discount");
  const m = getFeature(p, "price_main");
  const FREE = "\u0631\u0627\u06CC\u06AF\u0627\u0646";
  const TOMAN = " \u062A\u0648\u0645\u0627\u0646";
  const fmt = (v: string) => v === FREE ? FREE : formatFa(parseFloat(v.replace(/,/g, ""))) + TOMAN;
  return {
    main: m !== "\u2014" ? fmt(m) : p.price_monthly === 0 ? FREE : formatFa(p.price_monthly) + TOMAN,
    discount: d !== "\u2014" ? fmt(d) : "",
  };
}


/* ─── Floating chat messages ─────────────────────────────── */
interface FloatMsg { user: string; ai: string; model: string; color: string; }
const FLOAT_MSGS: FloatMsg[] = [
  { user: "یه ایمیل رسمی برای درخواست همکاری بنویس", ai: "موضوع: درخواست همکاری — با سلام، پیرو بررسی فعالیت‌های شرکت شما، مایلم امکان همکاری را بررسی کنیم...", model: "edrac-chat", color: "text-teal-400" },
  { user: "چرا این لوپ پایتون بی‌نهایت اجرا میشه؟", ai: "مشکل اینجاست: متغیر i داخل حلقه تغییر نمی‌کنه. خط ۷ رو به `i += 1` تغییر بده تا حل بشه.", model: "edrac-coder", color: "text-emerald-400" },
  { user: "تفاوت async/await با Promise چیه؟", ai: "هر دو برای کد ناهمزمان هستن، ولی async/await خوانایی بیشتری داره — به جای .then() می‌نویسی await و کد خطی‌تر میشه.", model: "gpt-5.3", color: "text-violet-400" },
  { user: "این متن انگلیسی رو به فارسی روان ترجمه کن", ai: "«هوش مصنوعی در حال تغییر بنیادی روش کار ماست و فرصت‌های بی‌سابقه‌ای برای کسب‌وکارها ایجاد می‌کند.»", model: "edrac-chat", color: "text-teal-400" },
  { user: "SQL query برای گرفتن ۱۰ کاربر فعال بنویس", ai: "SELECT user_id, name, last_login FROM users ORDER BY last_login DESC LIMIT 10; — ایندکس روی last_login هم اضافه کن.", model: "edrac-coder", color: "text-emerald-400" },
  { user: "خلاصه این مقاله ۵۰۰۰ کلمه‌ای رو بده", ai: "۳ نکته اصلی: ۱) رشد ۴۰٪ AI در ایران ۲) چالش زیرساخت شبکه ۳) فرصت بازار B2B داخلی — جزئیات بیشتر لازم داری؟", model: "edrac-chat", color: "text-teal-400" },
  { user: "React component برای فرم ورود بنویس", ai: "کامپوننت LoginForm با useState برای phone/password، validation فارسی، و submit handler آماده‌ست.", model: "edrac-coder", color: "text-emerald-400" },
  { user: "استراتژی بازاریابی برای اپ موبایل بده", ai: "پیشنهاد: ۱) ASO در کافه‌بازار ۲) اینفلوئنسر مارکتینگ ۳) برنامه ارجاع با تخفیف ۲۰٪ — کدوم رو اول شروع کنیم؟", model: "gpt-5.3", color: "text-violet-400" },
  { user: "این رزومه رو حرفه‌ای‌تر کن", ai: "تغییرات: عنوان به «مهندس ارشد نرم‌افزار» تغییر کرد، دستاوردها با عدد مشخص شد (مثلاً «بهبود ۳۰٪ سرعت»).", model: "edrac-chat", color: "text-teal-400" },
  { user: "باگ memory leak در Node.js رو پیدا کن", ai: "مشکل در event listener هست که removeListener نمی‌شه. خط ۴۳ رو ببین — داخل cleanup اضافه‌اش کن.", model: "edrac-coder", color: "text-emerald-400" },
];

const FLOAT_POSITIONS = [
  { top: "top-16",  side: "right-[8%]",  delay: "0s",    dur: "6s" },
  { top: "top-24",  side: "left-[6%]",   delay: "0.5s",  dur: "8s" },
  { top: "bottom-20", side: "left-[7%]", delay: "1s",    dur: "7s" },
  { top: "bottom-16", side: "right-[7%]",delay: "2s",    dur: "9s" },
];

const FLOAT_CODES = [
  { lines: ["def ask_ai():", "  return magic ✓"], delay: "0.5s", dur: "8s", pos: "top-24 left-[6%]" },
  { lines: ["const res = await", "edrac.chat() ✓"],  delay: "2s",  dur: "9s", pos: "bottom-16 right-[7%]" },
];

const FLOAT_INTERVAL = 3500;

const FEATURES: { icon: React.ReactNode; title: string; desc: string; gradient: string; glow: string }[] = [
  { icon: React.createElement(MessageSquare, { className: "h-5 w-5" }), title: "\u0686\u062A\u200C\u0628\u0627\u062A \u0647\u0648\u0634 \u0645\u0635\u0646\u0648\u0639\u06CC", desc: "\u0645\u06A9\u0627\u0644\u0645\u0647 \u0631\u0648\u0627\u0646 \u0628\u0647 \u0632\u0628\u0627\u0646 \u0641\u0627\u0631\u0633\u06CC \u0628\u0627 \u0628\u0647\u062A\u0631\u06CC\u0646 \u0645\u062F\u0644\u200C\u0647\u0627\u06CC \u062C\u0647\u0627\u0646\u06CC", gradient: "from-violet-500 to-purple-600", glow: "shadow-violet-500/25" },
  { icon: React.createElement(Code2, { className: "h-5 w-5" }), title: "\u062F\u0633\u062A\u06CC\u0627\u0631 \u06A9\u062F\u0646\u0648\u06CC\u0633\u06CC", desc: "\u0628\u0647\u06CC\u0646\u0647\u200C\u0633\u0627\u0632\u06CC\u200C\u0634\u062F\u0647 \u0628\u0631\u0627\u06CC \u0628\u0631\u0646\u0627\u0645\u0647\u200C\u0646\u0648\u06CC\u0633\u06CC \u0628\u0627 \u0645\u062F\u0644 edrac-coder", gradient: "from-emerald-500 to-teal-600", glow: "shadow-emerald-500/25" },
  { icon: React.createElement(Database, { className: "h-5 w-5" }), title: "\u067E\u0627\u06CC\u06AF\u0627\u0647 \u062F\u0627\u0646\u0634 (RAG)", desc: "\u0627\u062A\u0635\u0627\u0644 \u0628\u0647 \u0627\u0633\u0646\u0627\u062F \u0627\u062E\u062A\u0635\u0627\u0635\u06CC \u0634\u0645\u0627 \u0628\u0631\u0627\u06CC \u067E\u0627\u0633\u062E\u200C\u0647\u0627\u06CC \u062F\u0642\u06CC\u0642\u200C\u062A\u0631", gradient: "from-blue-500 to-cyan-600", glow: "shadow-blue-500/25" },
  { icon: React.createElement(Bot, { className: "h-5 w-5" }), title: "\u0686\u062A\u200C\u0628\u0627\u062A \u0633\u0641\u0627\u0631\u0634\u06CC", desc: "\u0633\u0627\u062E\u062A \u062F\u0633\u062A\u06CC\u0627\u0631 \u0647\u0648\u0634\u0645\u0646\u062F \u0627\u062E\u062A\u0635\u0627\u0635\u06CC \u0628\u0627 \u0634\u062E\u0635\u06CC\u062A \u0645\u0648\u0631\u062F \u0646\u0638\u0631 \u0634\u0645\u0627", gradient: "from-orange-500 to-amber-600", glow: "shadow-orange-500/25" },
  { icon: React.createElement(Globe, { className: "h-5 w-5" }), title: "\u0627\u062A\u0635\u0627\u0644 API", desc: "\u06CC\u06A9\u067E\u0627\u0631\u0686\u0647\u200C\u0633\u0627\u0632\u06CC \u0622\u0633\u0627\u0646 \u0628\u0627 \u0633\u06CC\u0633\u062A\u0645\u200C\u0647\u0627 \u0648 \u0627\u067E\u0644\u06CC\u06A9\u06CC\u0634\u0646\u200C\u0647\u0627\u06CC \u0634\u0645\u0627", gradient: "from-cyan-500 to-sky-600", glow: "shadow-cyan-500/25" },
  { icon: React.createElement(Server, { className: "h-5 w-5" }), title: "\u0632\u06CC\u0631\u0633\u0627\u062E\u062A \u0627\u062E\u062A\u0635\u0627\u0635\u06CC", desc: "\u0645\u0646\u0627\u0628\u0639 \u067E\u0631\u062F\u0627\u0632\u0634\u06CC \u0627\u062E\u062A\u0635\u0627\u0635\u06CC \u0648 \u0632\u06CC\u0631\u0633\u0627\u062E\u062A \u062E\u0635\u0648\u0635\u06CC \u0644\u0627\u06CC\u0647 \u06F2", gradient: "from-rose-500 to-pink-600", glow: "shadow-rose-500/25" },
];

const WHY: { icon: React.ReactNode; title: string; desc: string }[] = [
  { icon: React.createElement(Cpu, { className: "h-5 w-5" }), title: "\u0632\u06CC\u0631\u0633\u0627\u062E\u062A \u0642\u062F\u0631\u062A\u0645\u0646\u062F", desc: "\u0633\u0631\u0648\u0631\u0647\u0627\u06CC \u0627\u062E\u062A\u0635\u0627\u0635\u06CC \u0628\u0627 \u067E\u0627\u06CC\u062F\u0627\u0631\u06CC \u0648 \u0633\u0631\u0639\u062A \u0628\u0627\u0644\u0627" },
  { icon: React.createElement(Lock, { className: "h-5 w-5" }), title: "\u0627\u0645\u0646\u06CC\u062A \u0648 \u062D\u0631\u06CC\u0645 \u062E\u0635\u0648\u0635\u06CC", desc: "\u0637\u0631\u0627\u062D\u06CC \u062A\u0648\u0632\u06CC\u0639\u200C\u0634\u062F\u0647\u060C \u0645\u0637\u0627\u0628\u0642 GDPR\u060C \u062D\u0630\u0641 \u062F\u0627\u0626\u0645\u06CC \u0627\u0637\u0644\u0627\u0639\u0627\u062A" },
  { icon: React.createElement(Brain, { className: "h-5 w-5" }), title: "\u06CC\u0627\u062F\u06AF\u06CC\u0631\u06CC \u0645\u062F\u0627\u0648\u0645", desc: "Continuous Training \u0628\u0627 \u062F\u0627\u062F\u0647\u200C\u0647\u0627\u06CC \u0641\u0627\u0631\u0633\u06CC" },
  { icon: React.createElement(Shield, { className: "h-5 w-5" }), title: "\u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC \u0642\u0648\u06CC \u0627\u0632 \u0641\u0627\u0631\u0633\u06CC", desc: "\u0627\u062A\u0635\u0627\u0644 \u0628\u0647 RAG \u0648 \u062A\u0645\u0631\u06A9\u0632 \u0628\u0631 \u062F\u0627\u062F\u0647\u200C\u0647\u0627\u06CC \u0641\u0627\u0631\u0633\u06CC" },
  { icon: React.createElement(Zap, { className: "h-5 w-5" }), title: "\u0628\u0648\u0645\u06CC\u200C\u0633\u0627\u0632\u06CC \u0648 \u062A\u062E\u0635\u0635", desc: "\u0641\u0627\u06CC\u0646\u200C\u062A\u06CC\u0648\u0646 \u0628\u0627 \u0627\u062E\u0628\u0627\u0631 \u0648 \u062F\u0627\u062F\u0647\u200C\u0647\u0627\u06CC \u0627\u06CC\u0631\u0627\u0646\u06CC" },
  { icon: React.createElement(Sparkles, { className: "h-5 w-5" }), title: "\u0628\u062F\u0648\u0646 \u0646\u06CC\u0627\u0632 \u0628\u0647 \u0646\u0635\u0628", desc: "\u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0645\u0633\u062A\u0642\u06CC\u0645 \u0627\u0632 \u0645\u0631\u0648\u0631\u06AF\u0631\u060C \u0628\u062F\u0648\u0646 \u062F\u0627\u0646\u0634 \u0641\u0646\u06CC" },
];

/* ─── Component ──────────────────────────────────────────── */

const DEMO_QUESTIONS = [
  "یه ایمیل رسمی برای درخواست همکاری بنویس",
  "چرا این لوپ پایتون بی‌نهایت اجرا میشه؟",
  "تفاوت async/await با Promise چیه؟",
  "این متن انگلیسی رو به فارسی روان ترجمه کن",
  "SQL query برای گرفتن ۱۰ کاربر فعال بنویس",
];

const MockChat: React.FC<{ msgIdx: number[]; visible: boolean[] }> = ({ msgIdx }) => {
  const [qIdx, setQIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [phase, setPhase] = useState<"typing"|"waiting"|"answer">("typing");
  const [answerVisible, setAnswerVisible] = useState(false);
  const [history, setHistory] = useState<{ q: string; a: string; model: string; color: string }[]>([]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const q = DEMO_QUESTIONS[qIdx];

    if (phase === "typing") {
      if (typed.length < q.length) {
        timeout = setTimeout(() => setTyped(q.slice(0, typed.length + 1)), 55);
      } else {
        timeout = setTimeout(() => setPhase("waiting"), 500);
      }
    } else if (phase === "waiting") {
      timeout = setTimeout(() => { setAnswerVisible(true); setPhase("answer"); }, 1000);
    } else {
      timeout = setTimeout(() => {
        const msg = FLOAT_MSGS[qIdx % FLOAT_MSGS.length];
        setHistory(h => [...h.slice(-4), { q: DEMO_QUESTIONS[qIdx], a: msg.ai, model: msg.model, color: msg.color }]);
        setAnswerVisible(false);
        setTyped("");
        setPhase("typing");
        setQIdx((i) => (i + 1) % DEMO_QUESTIONS.length);
      }, 2800);
    }
    return () => clearTimeout(timeout);
  }, [phase, typed, qIdx]);

  const msg = FLOAT_MSGS[qIdx % FLOAT_MSGS.length];
  const shownHistory = history.slice(-2);
  const mobileHistory = history.slice(-1);

  return (
    <div className="relative h-full">
      {/* glow */}
      <div className="absolute inset-0 bg-violet-500/10 rounded-2xl blur-2xl" />
      <div className="relative bg-[#0e0e1a] border border-white/[0.07] rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col" dir="ltr">
        {/* titlebar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.05] bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <img src="/aptar_logo.png" className="w-5 h-5 object-contain rounded" alt="" />
            <span className="text-xs font-semibold text-gray-300">ادراک چت</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${msg.color} bg-white/5`}>{msg.model}</span>
          </div>
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
        </div>

        {/* messages */}
        <div className="p-3 sm:p-4 space-y-2 sm:space-y-3 flex-1 flex flex-col justify-end overflow-y-auto overflow-x-hidden">

          {/* history items — 2 on desktop, 1 on mobile */}
          {(window.innerWidth >= 1024 ? shownHistory : mobileHistory).map((item, i) => (
            <React.Fragment key={i}>
              <div className="flex justify-end">
                <div className="bg-white/5 border border-white/[0.05] rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-gray-400 max-w-[78%] text-right" dir="rtl">
                  {item.q}
                </div>
              </div>
              <div className="flex justify-start items-end gap-2">
                <img src="/aptar_logo.png" className="w-4 h-4 object-contain rounded-full flex-shrink-0 mb-0.5 opacity-60" alt="" />
                <div className="bg-gradient-to-br from-violet-600/15 to-indigo-600/10 border border-violet-500/15 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-gray-400 max-w-[80%] text-right" dir="rtl">
                  <span className={`text-[9px] font-bold ${item.color} block mb-0.5`}>{item.model}</span>
                  {item.a}
                </div>
              </div>
            </React.Fragment>
          ))}

          {/* current user message */}
          <div className="flex justify-end">
            <div className="bg-white/10 border border-white/15 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-white max-w-[78%] min-h-[28px] text-right" dir="rtl">
              {typed}
              {phase === "typing" && <span className="inline-block w-0.5 h-3 bg-violet-400 animate-pulse ml-0.5 align-middle" />}
            </div>
          </div>

          {/* AI answer */}
          {answerVisible && (
            <div className="flex justify-start items-end gap-2">
              <img src="/aptar_logo.png" className="w-5 h-5 object-contain rounded-full flex-shrink-0 mb-0.5" alt="" />
              <div className="bg-gradient-to-br from-violet-600/25 to-indigo-600/15 border border-violet-500/25 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-white max-w-[80%] text-right" dir="rtl">
                <span className={`text-[9px] font-bold ${msg.color} block mb-0.5`}>{msg.model}</span>
                {msg.ai}
              </div>
            </div>
          )}
          {phase === "waiting" && (
            <div className="flex justify-start items-end gap-2">
              <img src="/aptar_logo.png" className="w-5 h-5 object-contain rounded-full flex-shrink-0 mb-0.5" alt="" />
              <div className="bg-white/5 border border-white/[0.05] rounded-2xl rounded-tr-sm px-3 py-2">
                <div className="flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* input bar */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 bg-white/5 border border-white/[0.07] rounded-xl px-3 py-2">
            <span className="text-xs text-gray-500 flex-1">یه سوال بپرس...</span>
            <div className="w-6 h-6 rounded-lg bg-gradient-to-l from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Send className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LandingPage: React.FC = () => {
  const [plans, setPlans] = useState<PlanFromAPI[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [msgIdx, setMsgIdx] = useState([0, 3, 6, 9]);
  const [visible, setVisible] = useState([true, true, true, true]);

  useEffect(() => {
    const timers = FLOAT_POSITIONS.map((_, i) => {
      return setInterval(() => {
        setVisible(v => { const n = [...v]; n[i] = false; return n; });
        setTimeout(() => {
          setMsgIdx(idx => { const n = [...idx]; n[i] = (n[i] + 1) % FLOAT_MSGS.length; return n; });
          setVisible(v => { const n = [...v]; n[i] = true; return n; });
        }, 400);
      }, FLOAT_INTERVAL + i * 900);
    });
    return () => timers.forEach(clearInterval);
  }, []);

  useEffect(() => {
    profileAPI
      .listPlans()
      .then((list) => setPlans(Array.isArray(list) ? list.filter((p) => p.is_active !== false) : []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  return (
    <div dir="rtl" className="min-h-screen bg-[#070711] text-white overflow-x-hidden">

      {/* noise overlay */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.025]"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#070711]/75 backdrop-blur-2xl">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 bg-violet-500 rounded-lg blur-md opacity-50" />
              <img src="/aptar_logo.png" alt="ادراک" className="relative w-8 h-8 object-contain rounded-lg" />
            </div>
            <span className="text-sm font-bold tracking-wide">ادراک</span>
            <span className="text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/25 px-2 py-0.5 rounded-full tracking-widest">AI</span>
          </div>
          <div className="flex items-center gap-3">
          </div>
        </div>
      </nav>

      {/* ── اعلان رایگان شدن ── */}
      <div className="relative z-10 bg-gradient-to-l from-emerald-600/20 to-teal-600/20 border-b border-emerald-500/20 py-2.5 px-4 text-center">
        <p className="text-sm text-emerald-300 font-medium">
          ✨ مدل‌های جدید به ادراک اضافه شدن — همین الان امتحان کن
        </p>
      </div>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-10 sm:py-10 px-4 min-h-[90vh] sm:min-h-[calc(100vh-56px)] flex items-center">
        {/* bg blobs */}
        <div className="absolute top-0 right-1/3 w-[500px] h-[500px] bg-violet-700/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-indigo-700/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.015) 1px,transparent 1px)", backgroundSize: "64px 64px" }} />

        {/* ── floating bubbles (desktop only) ── */}
        <div className={`hidden sm:block absolute top-14 right-[4%] pointer-events-none select-none animate-[float_6s_ease-in-out_infinite] transition-opacity duration-400 z-0 ${visible[0] ? "opacity-[0.12]" : "opacity-0"}`}>
          <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 text-[10px] text-gray-800 font-medium w-40 shadow-xl">
            <div className="text-gray-400 text-[9px] mb-0.5">{FLOAT_MSGS[msgIdx[0]].user}</div>
            <div className="font-bold">{FLOAT_MSGS[msgIdx[0]].ai}</div>
          </div>
        </div>
        <div className={`hidden sm:block absolute top-20 left-[3%] pointer-events-none select-none animate-[float_8s_ease-in-out_0.5s_infinite] transition-opacity duration-400 z-0 ${visible[1] ? "opacity-[0.10]" : "opacity-0"}`}>
          <div className="bg-[#1a1a2e] border border-violet-500/30 rounded-xl px-3 py-2 font-mono text-[9px] w-36 shadow-xl">
            <div className="text-gray-400 text-[8px] font-sans mb-1">{FLOAT_MSGS[msgIdx[1]].user}</div>
            <span className="text-emerald-400">{FLOAT_MSGS[msgIdx[1]].ai}</span>
          </div>
        </div>
        <div className={`hidden sm:block absolute bottom-16 left-[3%] pointer-events-none select-none animate-[float_7s_ease-in-out_1s_infinite] transition-opacity duration-400 z-0 ${visible[2] ? "opacity-[0.12]" : "opacity-0"}`}>
          <div className="bg-gradient-to-br from-violet-600 to-indigo-600 rounded-2xl rounded-tr-sm px-3 py-2 text-[10px] text-white font-medium w-40 shadow-xl">
            <div className="text-violet-200 text-[9px] mb-0.5">{FLOAT_MSGS[msgIdx[2]].user}</div>
            <div className="font-bold">{FLOAT_MSGS[msgIdx[2]].ai}</div>
          </div>
        </div>
        <div className={`hidden sm:block absolute bottom-12 right-[4%] pointer-events-none select-none animate-[float_9s_ease-in-out_2s_infinite] transition-opacity duration-400 z-0 ${visible[3] ? "opacity-[0.10]" : "opacity-0"}`}>
          <div className="bg-[#1a1a2e] border border-emerald-500/25 rounded-xl px-3 py-2 text-[9px] w-36 shadow-xl">
            <div className="text-gray-400 text-[8px] mb-0.5">{FLOAT_MSGS[msgIdx[3]].user}</div>
            <div className="text-white font-bold">{FLOAT_MSGS[msgIdx[3]].ai}</div>
          </div>
        </div>
        {/* dots */}
        <div className="hidden sm:block absolute top-1/4 right-[20%] w-1 h-1 rounded-full bg-fuchsia-400 opacity-30 pointer-events-none animate-[pulse_4s_ease-in-out_infinite]" />
        <div className="hidden sm:block absolute top-1/3 left-[15%] w-1.5 h-1.5 rounded-full bg-cyan-400 opacity-25 pointer-events-none animate-[pulse_5s_ease-in-out_1s_infinite]" />
        <div className="hidden sm:block absolute bottom-1/3 right-[12%] w-1 h-1 rounded-full bg-violet-300 opacity-25 pointer-events-none animate-[pulse_3.5s_ease-in-out_0.5s_infinite]" />

        <div className="relative z-10 w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 sm:gap-14 items-center">

            {/* ── Left: text + CTA ── */}
            <div className="space-y-2 sm:space-y-16 text-center sm:text-right">
              {/* badge */}
              {/* <div className="inline-flex items-center gap-2 bg-white/5 border border-white/[0.07] text-gray-300 text-xs px-3 py-1.5 rounded-full mx-auto sm:mx-0">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                دسترسی رایگان — همین الان
              </div> */}

              {/* headline — one line */}
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
               ادراک
              </h1>

              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed max-w-sm">
                بنویس، ترجمه کن، کد بزن — بدون محدودیت
              </p>

              {/* model pills */}
              <div className="flex flex-wrap gap-1.5 justify-center sm:justify-start">
                {[
                  { name: "edrac-coder",       icon: "/aptar_logo.png", cls: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400", cover: false },
                  { name: "gpt-5.3",            icon: "/openai.png",     cls: "bg-violet-500/10 border-violet-500/20 text-violet-400",     cover: false },
                  { name: "claude-sonnet 4.5",  icon: "/sonnet.jpeg",    cls: "bg-orange-500/10 border-orange-500/20 text-orange-400",     cover: true  },
                  { name: "grok-4",             icon: "/xai.png",        cls: "bg-sky-500/10 border-sky-500/20 text-sky-400",             cover: false },
                  { name: "gemini-3.1 pro",     icon: "/gemini.png",     cls: "bg-pink-500/10 border-pink-500/20 text-pink-400",          cover: false },
                ].map((m) => (
                  <span key={m.name} className={`inline-flex items-center gap-1 border text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${m.cls}`}>
                    <span className="hidden sm:inline">{m.name}</span>
                    <img src={m.icon} className={`w-5 h-5 sm:w-3.5 sm:h-3.5 rounded ${m.cover ? "object-cover" : "object-contain"}`} alt={m.name} />
                  </span>
                ))}
              </div>

              {/* CTA — فقط دسکتاپ */}
              <div className="hidden sm:flex items-center gap-3 justify-center sm:justify-start">
                <Link to="/login" id="cta-hero" className="relative inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-semibold overflow-hidden group shadow-xl shadow-violet-500/25 hover:-translate-y-0.5 transition-transform">
                  <div className="absolute inset-0 bg-gradient-to-l from-violet-600 to-indigo-600" />
                  <div className="absolute inset-0 bg-gradient-to-l from-violet-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <Send className="relative h-3.5 w-3.5" />
                  <span className="relative">رایگان شروع کن</span>
                </Link>
                <span className="text-xs text-gray-500">بدون کارت بانکی</span>
              </div>
            </div>

            {/* ── Right / Bottom: mock chat (all screens) ── */}
            <div className="w-full max-w-sm mx-auto lg:max-w-none h-[260px] sm:h-auto flex flex-col gap-3">
              <MockChat msgIdx={msgIdx} visible={visible} />
              {/* CTA — فقط موبایل، زیر mock chat */}
              <div className="flex sm:hidden items-center gap-3 justify-center">
                <Link to="/login" id="cta-hero-mobile" className="relative inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-xl text-sm font-semibold overflow-hidden group shadow-xl shadow-violet-500/25 w-full justify-center">
                  <div className="absolute inset-0 bg-gradient-to-l from-violet-600 to-indigo-600" />
                  <Send className="relative h-3.5 w-3.5" />
                  <span className="relative">رایگان شروع کن</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-16 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* left: title */}
            <div className="text-center lg:text-right space-y-3 lg:sticky lg:top-24">
              <p className="text-xs text-violet-400 font-semibold tracking-widest uppercase">امکانات</p>
              <h2 className="text-2xl sm:text-3xl font-bold">پلتفرم ادراک چه امکاناتی دارد؟</h2>
              <p className="text-gray-500 text-sm">همه ابزارهای هوش مصنوعی در یک پلتفرم</p>
              <div className="hidden lg:block pt-4">
              </div>
            </div>
            {/* right: cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FEATURES.map((f, i) => (
                <div key={i} className="group relative bg-white/[0.03] border border-white/[0.05] rounded-2xl p-5 hover:border-white/15 transition-all duration-300 hover:-translate-y-0.5 overflow-hidden">
                  <div className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${f.gradient} opacity-0 group-hover:opacity-10 rounded-full blur-2xl transition-opacity duration-500 -translate-y-1/2 translate-x-1/2`} />
                  <div className={`w-9 h-9 bg-gradient-to-br ${f.gradient} rounded-xl flex items-center justify-center text-white mb-3 shadow-lg ${f.glow}`}>
                    {f.icon}
                  </div>
                  <h3 className="font-semibold text-sm mb-1 text-gray-100">{f.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Edrac ── */}
      <section className="py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 via-indigo-950/10 to-transparent pointer-events-none" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-violet-700/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* right: cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:order-1">
              {WHY.map((w, i) => (
                <div key={i} className="flex gap-3 bg-white/[0.03] border border-white/[0.05] rounded-xl p-4 hover:border-violet-500/20 hover:bg-violet-500/5 transition-all duration-300 group">
                  <div className="w-9 h-9 bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/15 rounded-xl flex items-center justify-center flex-shrink-0 text-violet-400 group-hover:border-violet-500/30 transition-colors">
                    {w.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1 text-gray-200">{w.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* left: title */}
            <div className="text-center lg:text-right space-y-3 lg:order-2 lg:sticky lg:top-24">
              <p className="text-xs text-violet-400 font-semibold tracking-widest uppercase">مزایا</p>
              <h2 className="text-2xl sm:text-3xl font-bold">چرا ادراک؟</h2>
              <p className="text-gray-500 text-sm">هوشیار، دقیق و متمرکز — طراحی‌شده برای پاسخ‌دهی سریع و قابل اتکا</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-4 text-center relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-violet-700/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(99,102,241,0.08),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-2xl mx-auto space-y-5">
          <h2 className="text-2xl sm:text-3xl font-bold">آماده‌اید هوش مصنوعی را هوشمند تجربه کنید؟</h2>
          <p className="text-gray-500 text-sm">دنیای هوش مصنوعی در دست شماست — همین الان شروع کنید.</p>
          <p className="text-xs text-gray-600">
            برای آخرین اخبار کانال بله{" "}
            <a href="https://ble.ir/edrac" className="text-violet-500 hover:text-violet-400 transition-colors">@edrac</a>{" "}
            را دنبال کنید
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <img src="/aptar_logo.png" alt="ادراک" className="w-5 h-5 object-contain opacity-60" />
            <span className="font-bold text-gray-400">ادراک</span>
            <span>— هوش مصنوعی ایرانی</span>
          </div>
          <div className="flex items-center gap-5 text-xs">
            <a href="https://edrac.ir" className="hover:text-gray-300 transition-colors">edrac.ir</a>
            <a href="https://ble.ir/edrac" className="hover:text-gray-300 transition-colors">بله</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
